import { Box, Stack, Typography, CircularProgress } from "@mui/joy";
import { useEffect, useState } from "react";

import { Button } from "../../../shared";
import { useDocument } from "../../../hooks/query";
import type { EstimateOption } from "../../../hooks/query";
import type { ContractPricingResult } from "../hooks/useFinanceBreakdown";
import { formatFinanceCurrency } from "../hooks/useFinanceBreakdown";
import { materialGlassPanelSx, MATERIAL_ROW } from "./LineItems/materialRowLayout";

const softDivider = "rgba(128, 128, 128, 0.2)";

type EstimateOptionsCompareBoardProps = {
    projectId?: string;
    options: EstimateOption[];
    openOptionId: string | null;
    loading?: boolean;
    refreshKey?: number;
    onOpen: (id: string) => void;
    onActivate: (id: string) => void;
    onClose?: () => void;
};

export default function EstimateOptionsCompareBoard({
    projectId,
    options,
    openOptionId,
    loading,
    refreshKey = 0,
    onOpen,
    onActivate,
    onClose,
}: EstimateOptionsCompareBoardProps) {
    const { useGetContractPricing } = useDocument();
    const pricingSvc = useGetContractPricing();
    const [pricingById, setPricingById] = useState<
        Record<string, ContractPricingResult | null>
    >({});
    const [pricingErrorById, setPricingErrorById] = useState<
        Record<string, string | null>
    >({});
    const [pricingLoading, setPricingLoading] = useState(false);

    useEffect(() => {
        if (!projectId || options.length === 0) return;

        let cancelled = false;
        const load = async () => {
            setPricingLoading(true);
            const nextPricing: Record<string, ContractPricingResult | null> = {};
            const nextErrors: Record<string, string | null> = {};
            await Promise.all(
                options.map(async (opt) => {
                    try {
                        const result = (await pricingSvc.mutateAsync({
                            ProjectId: projectId,
                            EstimateOptionId: opt.Id,
                        })) as ContractPricingResult;
                        nextPricing[opt.Id] = result;
                        nextErrors[opt.Id] = null;
                    } catch (err: any) {
                        nextPricing[opt.Id] = null;
                        nextErrors[opt.Id] =
                            err?.message || "Needs supplier pricing";
                    }
                }),
            );
            if (!cancelled) {
                setPricingById(nextPricing);
                setPricingErrorById(nextErrors);
                setPricingLoading(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, options.map((o) => o.Id).join("|"), refreshKey]);

    if (options.length < 2) return null;

    return (
        <Box
            sx={{
                ...materialGlassPanelSx,
                mb: 2,
            }}
        >
            <Box sx={{ px: MATERIAL_ROW.px, py: { xs: 2, sm: 2.5 } }}>
                <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography level="title-md">
                            Shingle estimate options
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                            Open an option to edit materials; set one Active for
                            the contract.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {(loading || pricingLoading) && (
                            <CircularProgress size="sm" />
                        )}
                        {onClose && (
                            <Button
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={onClose}
                            >
                                Hide
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: `repeat(${Math.min(options.length, 3)}, minmax(0, 1fr))`,
                        },
                        gap: 0,
                        border: `1px solid ${softDivider}`,
                        borderRadius: 0,
                        overflow: "hidden",
                        bgcolor: "background.surface",
                    }}
                >
                    {options.map((opt, index) => {
                        const pricing = pricingById[opt.Id];
                        const err = pricingErrorById[opt.Id];
                        const isActive = opt.Status === "ACTIVE";
                        const isOpen = openOptionId === opt.Id;

                        return (
                            <Box
                                key={opt.Id}
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    borderLeft: {
                                        xs: "none",
                                        md:
                                            index === 0
                                                ? "none"
                                                : `1px solid ${softDivider}`,
                                    },
                                    borderTop: {
                                        xs:
                                            index === 0
                                                ? "none"
                                                : `1px solid ${softDivider}`,
                                        md: "none",
                                    },
                                    bgcolor: isOpen
                                        ? "background.level1"
                                        : "background.surface",
                                }}
                            >
                                <Typography level="title-sm">
                                    {opt.Label}
                                    {isActive ? (
                                        <Typography
                                            component="span"
                                            level="body-xs"
                                            color="primary"
                                            sx={{ ml: 0.75 }}
                                        >
                                            Active
                                        </Typography>
                                    ) : null}
                                </Typography>
                                <Typography
                                    level="h4"
                                    sx={{
                                        mt: 1,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {pricing
                                        ? formatFinanceCurrency(
                                              pricing.TotalPrice,
                                          )
                                        : "—"}
                                </Typography>
                                {err && (
                                    <Typography
                                        level="body-xs"
                                        color="warning"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {err}
                                    </Typography>
                                )}
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1.5 }}
                                >
                                    <Button
                                        size="sm"
                                        variant={isOpen ? "soft" : "solid"}
                                        color="neutral"
                                        onClick={() => onOpen(opt.Id)}
                                    >
                                        Open
                                    </Button>
                                    {!isActive && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="neutral"
                                            onClick={() => onActivate(opt.Id)}
                                        >
                                            Set Active
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
